// components/LegalNoticeGenerator.jsx
import React, { useState, useCallback, useRef } from 'react';

const LegalNoticeGenerator = () => {
    const [loanAccount, setLoanAccount] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [noticeData, setNoticeData] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const inputRef = useRef(null);

    const API_BASE = process.env.REACT_APP_API_PATH || 'https://localhost:7032';

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        });
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setLoanAccount(value);
        setError(null);
        if (noticeData) {
            setNoticeData(null);
            setPdfUrl(null);
        }
    };

    const handleGenerate = useCallback(async () => {
        if (!loanAccount || loanAccount.length < 5) {
            setError('Please enter a valid loan account number (5-20 characters)');
            inputRef.current?.focus();
            return;
        }

        setIsLoading(true);
        setError(null);
        setPdfUrl(null);
        setShowPreview(false);

        try {
            // Fetch notice data
            const dataResponse = await fetch(`${API_BASE}legalnotice/data/${loanAccount}`);
            
            if (!dataResponse.ok) {
                if (dataResponse.status === 404) {
                    throw new Error(`Loan account ${loanAccount} not found in the system`);
                }
                const errorData = await dataResponse.json();
                throw new Error(errorData.error || 'Failed to fetch loan data');
            }

            const data = await dataResponse.json();
            setNoticeData(data);

            // Download PDF
            const pdfResponse = await fetch(`${API_BASE}legalnotice/pdf/${loanAccount}`);
            
            if (!pdfResponse.ok) {
                throw new Error('Failed to generate PDF');
            }

            const pdfBlob = await pdfResponse.blob();
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(url);

            // Auto-download
            const link = document.createElement('a');
            link.href = url;
            link.download = `Legal_Notice_${loanAccount}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err) {
            setError(err.message || 'Failed to generate legal notice');
            console.error('Generation error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [loanAccount, API_BASE]);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleGenerate();
        }
    };

    const handleDownloadPdf = useCallback(() => {
        if (pdfUrl) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `Legal_Notice_${loanAccount}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }, [pdfUrl, loanAccount]);

    const handleReset = () => {
        setLoanAccount('');
        setNoticeData(null);
        setPdfUrl(null);
        setShowPreview(false);
        setError(null);
        inputRef.current?.focus();
    };

    return (
        <div className="min-h-screen bg-gray-100 p-4 md:p-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex justify-center items-center gap-2 text-3xl font-bold text-gray-800">
                        ⚖️ Legal Notice Generator
                    </div>
                    <p className="text-gray-600 mt-2">
                        Generate a legal notice for loan recovery using the loan account number
                    </p>
                </div>

                {/* Input Section */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex-1 w-full">
                            <label htmlFor="loanAccount" className="block text-sm font-semibold text-gray-700 mb-2">
                                Loan Account Number <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    ref={inputRef}
                                    id="loanAccount"
                                    type="text"
                                    value={loanAccount}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter Loan Account Number (e.g., LN2025001)"
                                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg uppercase transition-all
                                        ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}
                                        focus:outline-none focus:ring-2`}
                                    disabled={isLoading}
                                    maxLength={20}
                                    autoFocus
                                />
                                {isLoading && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Enter 10-20 character alphanumeric loan account number
                            </p>
                            {error && (
                                <div className="mt-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                                    ⚠️ {error}
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-2 w-full md:w-auto">
                            <button
                                onClick={handleGenerate}
                                disabled={isLoading || !loanAccount}
                                className={`px-6 py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 min-w-[140px]
                                    ${isLoading || !loanAccount 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-blue-600 hover:bg-blue-700 active:scale-95'}`}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="animate-spin">⏳</span> Generating...
                                    </>
                                ) : (
                                    <>📄 Generate</>
                                )}
                            </button>
                            <button
                                onClick={handleReset}
                                disabled={isLoading}
                                className="px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-600 disabled:opacity-50"
                            >
                                ↻ Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* Notice Result */}
                {noticeData && pdfUrl && (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800">
                                    ✅ Notice Generated Successfully
                                </h2>
                                <p className="text-sm text-gray-600">
                                    {noticeData.borrowerName} • {formatCurrency(noticeData.outstandingAmount)}
                                </p>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => setShowPreview(!showPreview)}
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                                >
                                    {showPreview ? '📄 Hide Preview' : '👁️ Show Preview'}
                                </button>
                                <button
                                    onClick={handleDownloadPdf}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                >
                                    ⬇️ Download PDF
                                </button>
                            </div>
                        </div>

                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Borrower</p>
                                <p className="font-semibold text-gray-800">{noticeData.borrowerName}</p>
                                <p className="text-sm text-gray-600">{noticeData.borrowerAddress}</p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                                <p className="text-xs text-green-600 font-medium uppercase tracking-wider">Outstanding</p>
                                <p className="text-2xl font-bold text-red-600">{formatCurrency(noticeData.outstandingAmount)}</p>
                                <p className="text-sm text-gray-600">Loan Date: {formatDate(noticeData.loanDate)}</p>
                                <p className="text-sm text-gray-600">Due: {formatDate(noticeData.dueDate)}</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                                <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">Advocate</p>
                                <p className="font-semibold text-gray-800">{noticeData.advocateName}</p>
                                <p className="text-sm text-gray-600">{noticeData.advocateContact}</p>
                                <p className="text-xs text-gray-500 mt-1">Interest Rate: {noticeData.interestRate}%</p>
                            </div>
                        </div>

                        {showPreview && (
                            <div className="border-t border-gray-200">
                                <iframe
                                    src={pdfUrl}
                                    className="w-full h-[600px] bg-gray-100"
                                    title="Legal Notice PDF Preview"
                                    style={{ border: 'none' }}
                                />
                            </div>
                        )}

                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-wrap gap-3 text-sm text-gray-600">
                            <span>📋 Notice: LN/{new Date().getFullYear()}/{loanAccount.substring(0, Math.min(4, loanAccount.length))}</span>
                            <span className="text-gray-300">|</span>
                            <span>📅 Issued: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                            <span className="text-gray-300">|</span>
                            <span>⏳ Response: 15 days</span>
                        </div>
                    </div>
                )}

                {/* Database Info */}
                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                    <p className="font-medium">✅ Connected to Database</p>
                    <p className="text-xs text-green-600 mt-1">
                        Data is fetched from membermaster, grouploancontract, and vw_closing_balance tables.
                        {!noticeData && ' Enter a valid loan account number to fetch real data.'}
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .animate-spin {
                    animation: spin 0.7s linear infinite;
                }
            `}</style>
        </div>
    );
};

export default LegalNoticeGenerator;
